import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { SimliClient } from 'simli-client';
import { Loader2, AlertCircle, Wifi, WifiOff } from 'lucide-react';

/**
 * SimliAvatar - Real-time lip-synced avatar using Simli API
 * Fixed version with stable connection handling
 */
const SimliAvatar = forwardRef(({ 
  isPlaying, 
  isMuted, 
  onConnectionChange,
  sessionToken,
  iceServers,
  faceId = "5514e24d-6086-46a3-ace4-6a7264e5cb7c"
}, ref) => {
  const videoRef = useRef(null);
  const audioOutputRef = useRef(null);
  const simliClientRef = useRef(null);
  const audioContextRef = useRef(null);
  const isCleaningUpRef = useRef(false);
  const mountedRef = useRef(true);
  const initializingRef = useRef(false);
  
  // Audio streaming state
  const isStreamingAudioRef = useRef(false);
  const isPausedRef = useRef(false);
  const currentAudioDataRef = useRef(null);
  const currentPositionRef = useRef(0);
  const streamingIntervalRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  
  const [connectionState, setConnectionState] = useState('idle');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);

  // Create silence buffer once
  const silenceBuffer = useRef(new Uint8Array(6000)); // 187.5ms at 16kHz

  // Send silence to keep connection alive - called immediately on connect
  const sendSilence = useCallback(() => {
    if (simliClientRef.current && !isStreamingAudioRef.current) {
      try {
        simliClientRef.current.sendAudioData(silenceBuffer.current);
      } catch (e) {
        // Ignore errors
      }
    }
  }, []);

  // Start keep-alive interval
  const startKeepAlive = useCallback(() => {
    if (keepAliveIntervalRef.current) return;
    
    // Send silence immediately
    sendSilence();
    
    // Then continue sending every 150ms
    keepAliveIntervalRef.current = setInterval(() => {
      if (!isStreamingAudioRef.current) {
        sendSilence();
      }
    }, 150);
  }, [sendSilence]);

  const stopKeepAlive = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const stopStreaming = useCallback(() => {
    if (streamingIntervalRef.current) {
      clearInterval(streamingIntervalRef.current);
      streamingIntervalRef.current = null;
    }
    isStreamingAudioRef.current = false;
    setIsSpeaking(false);
  }, []);

  // Decode base64 audio to AudioBuffer
  const decodeBase64Audio = async (base64Audio) => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 16000
        });
      }

      const audioContext = audioContextRef.current;
      
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const audioBuffer = await audioContext.decodeAudioData(bytes.buffer);
      return audioBuffer;
    } catch (err) {
      console.error('Error decoding audio:', err);
      return null;
    }
  };

  // Convert AudioBuffer to PCM16 format for Simli
  const convertToPCM16 = (audioBuffer) => {
    const channelData = audioBuffer.getChannelData(0);
    
    let samples = channelData;
    if (audioBuffer.sampleRate !== 16000) {
      samples = resampleAudio(channelData, audioBuffer.sampleRate, 16000);
    }
    
    const pcm16 = new Int16Array(samples.length);
    for (let i = 0; i < samples.length; i++) {
      const s = Math.max(-1, Math.min(1, samples[i]));
      pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }

    return new Uint8Array(pcm16.buffer);
  };

  // Simple linear resampling
  const resampleAudio = (audioData, fromRate, toRate) => {
    const ratio = fromRate / toRate;
    const newLength = Math.round(audioData.length / ratio);
    const result = new Float32Array(newLength);
    
    for (let i = 0; i < newLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const srcIndexCeil = Math.min(srcIndexFloor + 1, audioData.length - 1);
      const fraction = srcIndex - srcIndexFloor;
      result[i] = audioData[srcIndexFloor] * (1 - fraction) + audioData[srcIndexCeil] * fraction;
    }
    
    return result;
  };

  // Stream audio chunks with proper pacing
  const streamAudioChunks = useCallback(() => {
    if (!currentAudioDataRef.current || !simliClientRef.current) return;
    
    const pcm16Data = currentAudioDataRef.current;
    const chunkSize = 6000; // ~187.5ms at 16kHz
    
    if (currentPositionRef.current < pcm16Data.length && !isPausedRef.current) {
      const endPos = Math.min(currentPositionRef.current + chunkSize, pcm16Data.length);
      const chunk = pcm16Data.slice(currentPositionRef.current, endPos);
      
      try {
        simliClientRef.current.sendAudioData(chunk);
        currentPositionRef.current = endPos;
      } catch (e) {
        console.warn('Failed to send audio chunk:', e);
      }
      
      if (currentPositionRef.current >= pcm16Data.length) {
        stopStreaming();
        currentPositionRef.current = 0;
        currentAudioDataRef.current = null;
        // Resume keep-alive after audio ends
        startKeepAlive();
      }
    }
  }, [stopStreaming, startKeepAlive]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    sendAudioForLipSync: async (audioBase64) => {
      if (!simliClientRef.current || connectionState !== 'connected') {
        console.warn('Simli not connected, cannot send audio');
        return false;
      }

      try {
        stopStreaming();
        stopKeepAlive(); // Stop keep-alive while streaming real audio
        
        const audioData = await decodeBase64Audio(audioBase64);
        if (!audioData) {
          throw new Error('Failed to decode audio');
        }

        const pcm16Data = convertToPCM16(audioData);
        
        currentAudioDataRef.current = pcm16Data;
        currentPositionRef.current = 0;
        isPausedRef.current = false;
        isStreamingAudioRef.current = true;
        setIsSpeaking(true);
        
        // Stream at 150ms intervals
        streamingIntervalRef.current = setInterval(streamAudioChunks, 150);
        
        return true;
      } catch (err) {
        console.error('Error preparing audio for Simli:', err);
        startKeepAlive(); // Resume keep-alive on error
        return false;
      }
    },

    pauseAudio: () => {
      console.log('Simli: Pausing audio at position', currentPositionRef.current);
      isPausedRef.current = true;
      setIsSpeaking(false);
      
      // Start sending silence again
      startKeepAlive();
    },

    resumeAudio: () => {
      if (!currentAudioDataRef.current || currentPositionRef.current >= currentAudioDataRef.current.length) {
        console.log('Simli: No audio to resume');
        return false;
      }
      
      console.log('Simli: Resuming from position', currentPositionRef.current);
      stopKeepAlive();
      isPausedRef.current = false;
      isStreamingAudioRef.current = true;
      setIsSpeaking(true);
      
      if (!streamingIntervalRef.current) {
        streamingIntervalRef.current = setInterval(streamAudioChunks, 150);
      }
      
      return true;
    },

    stopAudio: () => {
      console.log('Simli: Stopping audio');
      stopStreaming();
      isPausedRef.current = false;
      currentPositionRef.current = 0;
      currentAudioDataRef.current = null;
      startKeepAlive();
    },

    syncPosition: (progressRatio) => {
      if (!currentAudioDataRef.current) return;
      currentPositionRef.current = Math.floor(progressRatio * currentAudioDataRef.current.length);
    },

    isPaused: () => isPausedRef.current,
    isConnected: () => connectionState === 'connected',
    getConnectionState: () => connectionState
  }), [connectionState, stopStreaming, streamAudioChunks, startKeepAlive, stopKeepAlive]);

  // Initialize Simli client
  useEffect(() => {
    // Strict mode protection - prevent double initialization
    if (!sessionToken || initializingRef.current || simliClientRef.current) {
      console.log('Simli: Skipping init - already initialized or initializing');
      return;
    }
    
    initializingRef.current = true;
    mountedRef.current = true;
    isCleaningUpRef.current = false;

    const initializeSimli = async () => {
      try {
        if (!mountedRef.current || isCleaningUpRef.current) {
          initializingRef.current = false;
          return;
        }
        
        setConnectionState('connecting');
        setError(null);
        onConnectionChange?.('connecting');

        const simliClient = new SimliClient();
        
        simliClient.Initialize({
          session_token: sessionToken,
          faceID: faceId,
          handleSilence: true,
          maxSessionLength: 3600,
          maxIdleTime: 600,
          videoRef: videoRef.current,
          audioRef: audioOutputRef.current,
          enableConsoleLogs: true,
        });

        simliClient.on('connected', () => {
          if (!mountedRef.current) return;
          console.log('Simli: Connected - starting keep-alive');
          setConnectionState('connected');
          onConnectionChange?.('connected');
          startKeepAlive();
        });
        
        simliClient.on('disconnected', () => {
          if (!mountedRef.current || isCleaningUpRef.current) return;
          console.log('Simli: Disconnected');
          setConnectionState('disconnected');
          onConnectionChange?.('disconnected');
          stopKeepAlive();
        });
        
        simliClient.on('failed', (err) => {
          if (!mountedRef.current) return;
          console.error('Simli: Failed', err);
          setConnectionState('failed');
          setError('Connection failed');
          onConnectionChange?.('failed');
        });
        
        simliClient.on('speaking', () => {
          if (mountedRef.current) setIsSpeaking(true);
        });
        
        simliClient.on('silent', () => {
          if (mountedRef.current && !isStreamingAudioRef.current) {
            setIsSpeaking(false);
          }
        });

        simliClientRef.current = simliClient;
        await simliClient.start();

      } catch (err) {
        if (!mountedRef.current) return;
        console.error('Simli init error:', err);
        setError(err.message);
        setConnectionState('failed');
        onConnectionChange?.('failed');
        initializingRef.current = false;
      }
    };

    // Delay to avoid React strict mode double-init race condition
    const timeoutId = setTimeout(initializeSimli, 500);

    return () => {
      clearTimeout(timeoutId);
      
      // Only cleanup if we actually initialized
      if (!simliClientRef.current) {
        initializingRef.current = false;
        return;
      }
      
      mountedRef.current = false;
      isCleaningUpRef.current = true;
      stopKeepAlive();
      stopStreaming();
      
      const client = simliClientRef.current;
      simliClientRef.current = null;
      
      if (client) {
        try {
          console.log('Simli: Closing connection on cleanup');
          client.close();
        } catch (e) {
          console.log('Simli cleanup error:', e);
        }
      }
      
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch (e) {
          // Ignore
        }
        audioContextRef.current = null;
      }
      
      initializingRef.current = false;
    };
  }, [sessionToken, faceId, onConnectionChange, startKeepAlive, stopKeepAlive, stopStreaming]);

  // Handle mute
  useEffect(() => {
    if (audioOutputRef.current) {
      audioOutputRef.current.muted = isMuted;
    }
  }, [isMuted]);

  return (
    <div className="avatar-container relative w-full h-full flex flex-col items-center justify-center" data-testid="simli-avatar">
      <div className="relative">
        {isSpeaking && (
          <div 
            className="absolute -inset-8 rounded-3xl blur-2xl transition-opacity duration-300 z-0"
            style={{ background: 'radial-gradient(circle, rgba(0,86,210,0.25) 0%, transparent 70%)' }}
          />
        )}
        
        <div 
          className="relative overflow-hidden rounded-2xl"
          style={{
            width: '300px',
            height: '300px',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            boxShadow: isSpeaking 
              ? '0 0 50px rgba(0, 86, 210, 0.3), 0 20px 60px rgba(0,0,0,0.4)' 
              : '0 15px 50px rgba(0,0,0,0.3)'
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          
          <audio ref={audioOutputRef} autoPlay className="hidden" />
          
          {connectionState === 'connecting' && (
            <div className="absolute inset-0 bg-slate-900/90 flex flex-col items-center justify-center">
              <Loader2 className="w-10 h-10 text-[#0056D2] animate-spin mb-3" />
              <p className="text-white text-sm">Connecting to avatar...</p>
            </div>
          )}
          
          {(connectionState === 'failed' || connectionState === 'disconnected') && (
            <div className="absolute inset-0 bg-slate-900/95 flex flex-col items-center justify-center p-4">
              <AlertCircle className="w-10 h-10 text-amber-500 mb-3" />
              <p className="text-amber-400 text-sm text-center">
                {connectionState === 'failed' ? 'Connection failed' : 'Reconnecting...'}
              </p>
            </div>
          )}
          
          <div className="absolute top-3 left-3 z-10">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
              connectionState === 'connected' 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                : connectionState === 'connecting'
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
            }`}>
              {connectionState === 'connected' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              <span className="capitalize">{connectionState}</span>
            </div>
          </div>
          
          {isSpeaking && connectionState === 'connected' && (
            <div className="absolute top-3 right-3 z-10">
              <div className="flex items-center gap-1.5 bg-red-500 rounded-full px-3 py-1.5 shadow-lg">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-bold">LIVE</span>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-4 text-center">
          <div className="inline-block bg-gradient-to-r from-slate-800/90 to-slate-700/90 backdrop-blur-sm rounded-xl px-6 py-3 shadow-xl border border-slate-600/30">
            <p className="text-white font-semibold text-lg">AI Instructor</p>
            <p className="text-[#0056D2] text-sm font-medium">Powered by Simli</p>
          </div>
        </div>
      </div>
      
      {isSpeaking && connectionState === 'connected' && (
        <div className="mt-6 flex items-center gap-3 bg-slate-800/60 backdrop-blur-sm rounded-full px-6 py-3 border border-slate-700/50">
          <div className="flex items-center gap-1">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div
                key={i}
                className="w-1 bg-gradient-to-t from-[#0056D2] to-[#00A3FF] rounded-full animate-pulse"
                style={{ height: `${8 + Math.sin(Date.now() / 100 + i) * 8}px`, animationDelay: `${i * 50}ms` }}
              />
            ))}
          </div>
          <span className="text-white/90 text-sm font-medium ml-2">Speaking</span>
        </div>
      )}
      
      {!isSpeaking && connectionState === 'connected' && (
        <div className="mt-6 text-slate-400 text-sm flex items-center gap-2 bg-slate-800/40 rounded-full px-4 py-2">
          <div className="w-8 h-8 rounded-full bg-[#0056D2]/20 flex items-center justify-center">
            <svg className="w-4 h-4 text-[#0056D2]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </div>
          <span>Ready to speak</span>
        </div>
      )}
    </div>
  );
});

SimliAvatar.displayName = 'SimliAvatar';

export default SimliAvatar;
